import React, { ReactElement, useState } from 'react'
import { Link } from 'react-router-dom'
import { Button } from 'primereact/button'
import { Column } from 'primereact/column'

import ConnectorStatus from '../../../lib/connector-status/index'
import isSchukoEnabled from '../../../lib/isSchukoEnabled'
import isAura from '../../../lib/isAura'
import {
    ChargePoint,
    Connector,
    LoadBalancerGroup,
    useLoadBalancerGroupDeleteChargePointFromGroupMutation,
} from '../../../common/redux/api/web-api'
import EmptyListRefresh from '../../../common/components/EmptyListRefresh'
import { isAdminDomain } from '../../../lib/currentDomain'
import isOnline from '../../../lib/isOnline'
import { isChargingConnector } from '../../../lib/findChargingConnector'
import { theme } from '../../../lib/theme'
import SignalIcon from '../../../lib/icons/signal-icon/signal-icon'
import useText from '../../../common/hooks/useText'
import { confirm } from '../../../common/uikit/ConfirmDialog'
import { Table } from '../../../common/uikit/Table'
import { useToast } from '../../../common/uikit/ToastProvider'
import { Panel } from '../../../common/uikit/Panel'
import { getSupplyPhase } from '../../../common/domain/electrical-settings'

import { LoadBalancerImportChargePointDialog } from './dialogs/load-balancer-import-charge-point-dialog'
import { EditChargePointSettingsDialog } from './dialogs/edit-charge-point-settings-dialog'

type LBChargePointListType = {
    chargePoints: ChargePoint[]
    isGroupActive: boolean
    isLoading: boolean
    isFetching: boolean
    group: LoadBalancerGroup
    updateChargers: () => void
}

export const LBChargePointList = ({
    chargePoints,
    isGroupActive,
    isLoading,
    isFetching,
    group,
    updateChargers,
}: LBChargePointListType): ReactElement => {
    const text = useText()
    const toast = useToast()
    const [showModal, setShowModal] = useState(false)
    const [selectedChargePoint, setSelectedChargePoint] = useState<ChargePoint | null>(null)
    const [showImportModal, setShowImportModal] = useState(false)

    const openModal = (chargePoint: ChargePoint) => {
        setSelectedChargePoint(chargePoint)
        setShowModal(true)
    }

    const closeModal = (shouldRefreshChargePoints = false) => {
        setShowModal(false)
        if (shouldRefreshChargePoints) {
            updateChargers()
        }
    }

    const schukoEnabled = (chargePoint: ChargePoint) => {
        if (isAura(chargePoint)) return false
        if (!chargePoint.isOcpp) {
            return true
        }
        return isSchukoEnabled(chargePoint)
    }

    const [removeCharger] = useLoadBalancerGroupDeleteChargePointFromGroupMutation()
    const handleRemoveChargePointFromGroup = async (chargePointId: string) => {
        if (!chargePointId) return

        const confirmed = await confirm({
            header: text('lblProceedConfirmation'),
            message: text('msgRemoveChargepointFromGroup'),
            variant: 'danger',
        })
        if (!confirmed) return

        try {
            await removeCharger({ id: group?.id ?? 0, chargePointId: chargePointId.toString() }).unwrap()
            toast.notifySuccess(text('msgChargerRemoved'))
        } catch {
            toast.notifyError(text('msgCouldNotRemoveCharger'))
        }
    }

    const renderStatus = (chargePoint: ChargePoint) => (
        <div className="inline-block p-1 ml-3" data-testid="chargepoint-status">
            {chargePoint?.connectors?.map((connector: Connector) => {
                if (!isOnline(chargePoint) && isChargingConnector(connector)) {
                    return (
                        <SignalIcon
                            key={connector?.connectorId}
                            fill={theme.brandColors.CA_Coral}
                            size={16}
                            title={text('lblOfflineConnectorName', {
                                name: connector?.name,
                            })}
                        />
                    )
                }

                if (!isOnline(chargePoint)) {
                    return null
                }

                if (connector?.connectorFunctionType === 'Schuko' && !schukoEnabled(chargePoint)) {
                    return null
                }

                return (
                    <ConnectorStatus
                        connector={connector}
                        isOcpp={chargePoint?.isOcpp}
                        key={connector?.connectorId}
                        productId={chargePoint?.productId}
                    />
                )
            })}
        </div>
    )

    const renderConnectorSettings = (chargePoint: ChargePoint) => {
        const installationPhaseMode = chargePoint.connectors?.at(0)?.installationPhaseMode

        return (
            <div className="flex align-items-center justify-content-between" data-testid="chargepoint-settings">
                {chargePoint?.connectors
                    ?.filter((connector) => isChargingConnector(connector))
                    ?.map((connector) => {
                        if (installationPhaseMode === '1P') {
                            return (
                                <span key={connector?.connectorId}>
                                    1P ({connector?.installationPhase} - {connector?.installationCurrent} A)
                                </span>
                            )
                        }

                        if (installationPhaseMode === '3P') {
                            return <span key={connector?.connectorId}>3P ({connector?.installationCurrent} A)</span>
                        }

                        return null
                    })}
            </div>
        )
    }

    const renderOfflinePhase = (chargePoint: ChargePoint, connector: Connector) => {
        const phase = connector.loadBalancerOfflinePhase
        if (!chargePoint.isOcpp) return phase

        return (
            <span
                title={text('msgLoadBalancerOfflineSupplyPhase', {
                    phase: phase ?? text('lblUnknown'),
                    rotation: chargePoint.connectorPhaseRotation ?? text('lblUnknown'),
                })}
            >
                {getSupplyPhase(phase, chargePoint.connectorPhaseRotation) ?? text('lblUnknown')}
            </span>
        )
    }

    const renderOfflineSettings = (chargePoint: ChargePoint) => (
        <div className="flex align-items-center justify-content-between" data-testid="chargepoint-offline-settings">
            <div>
                {chargePoint?.connectors
                    ?.filter((connector) => isChargingConnector(connector))
                    ?.map((connector) => (
                        <div key={connector?.connectorId} data-testid="connector-offline-settings">
                            {renderOfflinePhase(chargePoint, connector)} - {connector?.loadBalancerOfflineCurrent} A
                        </div>
                    ))}
            </div>
        </div>
    )

    const renderAssignedSettings = (chargePoint: ChargePoint) => (
        <div className="flex flex-column" data-testid="chargepoint-assigned">
            {chargePoint?.connectors?.map((connector) => {
                const loadBalancerCurrent = connector?.loadBalancerCurrent ?? 0

                if (isChargingConnector(connector)) {
                    if (isOnline(chargePoint) && loadBalancerCurrent > 0) {
                        const isThreePhaseWithoutSelectedPhase =
                            connector?.installationPhaseMode === '3P' && connector?.loadBalancerPhase === null

                        return (
                            <div key={connector?.connectorId}>
                                {isThreePhaseWithoutSelectedPhase
                                    ? `3P - ${loadBalancerCurrent} ${text('lblAmpereShort')}`
                                    : `${connector?.loadBalancerPhase} - ${loadBalancerCurrent} ${text('lblAmpereShort')}`}
                            </div>
                        )
                    }

                    if (isOnline(chargePoint)) {
                        return (
                            <div className="ml-5" key={connector?.connectorId}>
                                &nbsp;
                            </div>
                        )
                    }

                    return (
                        <div key={connector?.connectorId}>
                            {text('lblOffline')}: {renderOfflinePhase(chargePoint, connector)} -{' '}
                            {connector?.loadBalancerOfflineCurrent} {text('lblAmpereShort')}
                        </div>
                    )
                }

                if (isOnline(chargePoint) && loadBalancerCurrent > 0) {
                    return (
                        <div key={connector?.connectorId}>
                            <span className="fa icon-socket"></span>
                            <span>
                                {connector?.loadBalancerPhase} - {loadBalancerCurrent} {text('lblAmpereShort')}
                            </span>
                        </div>
                    )
                }

                if (isOnline(chargePoint) && connector?.loadBalancerCurrent === 0) {
                    return (
                        <div key={connector?.connectorId}>
                            <span className="fa icon-socket"></span> ---
                        </div>
                    )
                }

                if (!isOnline(chargePoint)) {
                    return (
                        <div key={connector?.connectorId}>
                            <span className="fa icon-socket"></span> {text('lblOffline')}
                        </div>
                    )
                }

                return null
            })}
        </div>
    )

    const renderActions = (chargePoint: ChargePoint) =>
        !isAdminDomain ? (
            <span
                className="flex justify-content-end gap-2 w-full align-items-center"
                data-testid="chargepoint-menu-settings"
            >
                <Button
                    size="small"
                    icon="pi pi-pencil"
                    rounded
                    outlined
                    title={text('lblLoadBalancerEditTooltip')}
                    type="button"
                    severity="info"
                    disabled={isGroupActive}
                    onClick={() => openModal(chargePoint)}
                    data-testid="btn-edit-charger"
                />
                <Button
                    size="small"
                    icon="pi pi-times"
                    rounded
                    outlined
                    title={text('btnRemove')}
                    type="button"
                    severity="danger"
                    disabled={isGroupActive}
                    onClick={() => handleRemoveChargePointFromGroup(chargePoint?.id)}
                    data-testid="btn-delete-charger"
                />
            </span>
        ) : null

    const headerActions = !isAdminDomain ? (
        <>
            <Button
                size="small"
                icon="pi pi-refresh"
                onClick={() => updateChargers()}
                aria-haspopup
                title={text('btnRefresh')}
                className="p-button-plain"
                data-testid="refresh-button"
            />
            <Button
                size="small"
                icon="pi pi-cloud-upload"
                onClick={() => setShowImportModal(true)}
                title={text('lblChargersImportModal')}
                disabled={isGroupActive}
                className="p-button-plain"
                data-testid="import-button"
            />
        </>
    ) : undefined

    return (
        <>
            <Panel
                title={text('lblChargers')}
                testId="chargepoints-list-lBGroup"
                headerIcon={null}
                headerActions={headerActions}
            >
                {chargePoints?.length === 0 && (
                    <EmptyListRefresh
                        emptyText={text('msgNoChargePointsInLBGroup')}
                        isFetching={isFetching}
                        action={updateChargers}
                        label={text('btnRefresh')}
                        data-testid="chargepoints-empty-list"
                    />
                )}
                {group && chargePoints?.length > 0 && (
                    <Table
                        dataKey="id"
                        data-testid="chargepoints-list"
                        value={chargePoints}
                        loading={isLoading}
                        fetching={isFetching}
                        simplified
                    >
                        <Column
                            field="id"
                            header={text('lblSerialNumberColumnHeader')}
                            bodyClassName="vertical-align-middle"
                            body={(chargePoint: ChargePoint) => (
                                <Link to={`/chargepoints/${chargePoint.id}`} data-testid="chargepoint-id">
                                    {chargePoint?.id}
                                </Link>
                            )}
                        />
                        <Column
                            field="status"
                            header={text('lblStatus')}
                            bodyClassName="vertical-align-middle"
                            body={renderStatus}
                        />
                        <Column
                            field="settings"
                            header={text('lblSettings')}
                            bodyClassName="vertical-align-middle"
                            body={renderConnectorSettings}
                        />
                        <Column
                            field="offlineSettings"
                            header={text('lblOffline')}
                            bodyClassName="vertical-align-middle"
                            body={renderOfflineSettings}
                        />
                        <Column
                            field="assignedSettings"
                            header={text('lblAssigned')}
                            bodyClassName="vertical-align-middle"
                            body={renderAssignedSettings}
                        />
                        <Column bodyClassName="vertical-align-middle text-right" body={renderActions} />
                    </Table>
                )}
            </Panel>

            <EditChargePointSettingsDialog
                visible={showModal}
                chargePoint={selectedChargePoint}
                closeModal={closeModal}
            />

            <LoadBalancerImportChargePointDialog
                visible={showImportModal}
                setVisible={setShowImportModal}
                group={group}
                chargePointsInGroup={chargePoints}
                updateChargePoints={() => updateChargers()}
            />
        </>
    )
}

export default LBChargePointList
